import { COUNTRY_LANGUAGES, SUPPORTED_COUNTRIES, formatLocale } from '@src/config/localisation/locale-config.constants'
import type { TAbsoluteLocale, TCountry, TRegionDetail } from '@src/config/localisation/locale-config.types'

import { getStandardLocale } from './i18n.utils'

const isValidLocaleFormat = (locale: string): boolean => {
  // Accept both `en-US` and `en-us` (and any casing mix); normalize via getStandardLocale.
  const localePattern = /^[a-z]{2}-[a-z]{2}$/i
  return localePattern.test(locale)
}

const isValidCountryFormat = (country: string): boolean => {
  const countryPattern = /^[a-z]{2}$/i
  return countryPattern.test(country)
}

interface RegionAliases {
  country?: Record<string, string>
}

// Use case: map user-facing URL country aliases (e.g. /uk) to canonical country codes (gb).
const getCanonicalCountryFromPathPrefix = (countryPrefix: string, regionDetails?: TRegionDetail): TCountry => {
  const normalized = countryPrefix.toLowerCase()
  const regionAliases = regionDetails?.regionAliases as RegionAliases | undefined
  const countryAliases: Record<string, string> = regionAliases?.country ?? {}
  return (countryAliases[normalized]?.toLowerCase() ?? normalized) as TCountry
}

export const extractLocaleFromUrlPath = (regionDetails?: TRegionDetail): string | null => {
  // Use case: parse locale from either full locale prefixes (/en-gb) or country/display prefixes (/uk),
  // with dynamic RMS alias support when regionDetails is available.
  if (typeof window === 'undefined') {
    return null
  }

  const pathSegments = window.location.pathname.split('/')
  const potentialLocale = pathSegments[1]

  if (!potentialLocale) return null

  // Locale prefix segment can be:
  // 1. Full locale: `en-us` or `en-US`
  // 2. Country-only: `us`, `ca`, `gb`, or display-code alias like `uk`
  if (potentialLocale.includes('-')) {
    if (!isValidLocaleFormat(potentialLocale)) return null
    try {
      return getStandardLocale(potentialLocale.toLowerCase() as TAbsoluteLocale)
    } catch (parseError) {
      console.log('Error parsing locale from URL path:', parseError)
      return null
    }
  }

  if (isValidCountryFormat(potentialLocale)) {
    const normalized = potentialLocale.toLowerCase()
    const canonicalCountry = getCanonicalCountryFromPathPrefix(normalized, regionDetails)

    // If we have dynamic regionDetails, prefer it as the source of truth for default locale.
    if (regionDetails?.regions[canonicalCountry]?.defaultLanguageIsoCode) {
      return getStandardLocale(regionDetails.regions[canonicalCountry].defaultLanguageIsoCode)
    }

    // Otherwise, only accept canonical country codes.
    if (!SUPPORTED_COUNTRIES.includes(canonicalCountry)) return null

    const defaultLanguage = COUNTRY_LANGUAGES[canonicalCountry][0]
    if (!defaultLanguage) return null

    try {
      const absoluteLocale = formatLocale(canonicalCountry, defaultLanguage)
      return getStandardLocale(absoluteLocale as TAbsoluteLocale)
    } catch (parseError) {
      console.log('Error deriving locale from country URL prefix:', parseError)
      return null
    }
  }

  return null
}

export const getLocaleFromUrlPath = (defaultLocale: string, regionDetails?: TRegionDetail): string => {
  // Use case: keep existing default-locale fallback while enabling alias-aware extraction.
  const urlLocale = extractLocaleFromUrlPath(regionDetails)
  return urlLocale ?? defaultLocale
}
